/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {InjectionToken, Injector} from '../di';
import {ViewEncapsulation} from '../metadata/view';
/**
 * @deprecated Use `RendererType2` (and `Renderer2`) instead.
 */
export class RenderComponentType {
/**
 * @param {?} id
 * @param {?} templateUrl
 * @param {?} slotCount
 * @param {?} encapsulation
 * @param {?} styles
 * @param {?} animations
 */
constructor(
public id: string,
public templateUrl: string,
public slotCount: number,
public encapsulation: ViewEncapsulation,
public styles: Array<string|any[]>,
public animations: any) {}
}

function RenderComponentType_tsickle_Closure_declarations() {
/** @type {?} */
RenderComponentType.prototype.id;
/** @type {?} */
RenderComponentType.prototype.templateUrl;
/** @type {?} */
RenderComponentType.prototype.slotCount;
/** @type {?} */
RenderComponentType.prototype.encapsulation;
/** @type {?} */
RenderComponentType.prototype.styles;
/** @type {?} */
RenderComponentType.prototype.animations;
}

/**
 * @deprecated Debug info is handeled internally in the view engine now.
 * @abstract
 */
export abstract class RenderDebugInfo {
/**
 * @abstract
 * @return {?}
 */
injector() {}
/**
 * @abstract
 * @return {?}
 */
component() {}
/**
 * @abstract
 * @return {?}
 */
providerTokens() {}
/**
 * @abstract
 * @return {?}
 */
references() {}
/**
 * @abstract
 * @return {?}
 */
context() {}
/**
 * @abstract
 * @return {?}
 */
source() {}
}

/**
 * @deprecated Use the `Renderer2` instead.
 */
export interface DirectRenderer {
  remove(node: any): void;
  appendChild(node: any, parent: any): void;
  insertBefore(node: any, refNode: any): void;
  nextSibling(node: any): any;
  parentElement(node: any): any;
}
/**
 * @deprecated Use the `Renderer2` instead.
 * @abstract
 */
export abstract class Renderer {
/**
 * @abstract
 * @param {?} selectorOrNode
 * @param {?=} debugInfo
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?} name
 * @param {?=} debugInfo
 * @return {?}
 */
createElement(parentElement: any, name: string, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} hostElement
 * @return {?}
 */
createViewRoot(hostElement: any) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?=} debugInfo
 * @return {?}
 */
createTemplateAnchor(parentElement: any, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?} value
 * @param {?=} debugInfo
 * @return {?}
 */
createText(parentElement: any, value: string, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?} nodes
 * @return {?}
 */
projectNodes(parentElement: any, nodes: any[]) {}
/**
 * @abstract
 * @param {?} node
 * @param {?} viewRootNodes
 * @return {?}
 */
attachViewAfter(node: any, viewRootNodes: any[]) {}
/**
 * @abstract
 * @param {?} viewRootNodes
 * @return {?}
 */
detachView(viewRootNodes: any[]) {}
/**
 * @abstract
 * @param {?} hostElement
 * @param {?} viewAllNodes
 * @return {?}
 */
destroyView(hostElement: any, viewAllNodes: any[]) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listen(renderElement: any, name: string, callback: Function) {}
/**
 * @abstract
 * @param {?} target
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listenGlobal(target: string, name: string, callback: Function) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setElementProperty(renderElement: any, propertyName: string, propertyValue: any) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} attributeName
 * @param {?} attributeValue
 * @return {?}
 */
setElementAttribute(renderElement: any, attributeName: string, attributeValue: string) {}
/**
 * Used only in debug mode to serialize property changes to dom nodes as attributes.
 * @abstract
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setBindingDebugInfo(renderElement: any, propertyName: string, propertyValue: string) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} className
 * @param {?} isAdd
 * @return {?}
 */
setElementClass(renderElement: any, className: string, isAdd: boolean) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} styleName
 * @param {?} styleValue
 * @return {?}
 */
setElementStyle(renderElement: any, styleName: string, styleValue: string) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} methodName
 * @param {?=} args
 * @return {?}
 */
invokeElementMethod(renderElement: any, methodName: string, args?: any[]) {}
/**
 * @abstract
 * @param {?} renderNode
 * @param {?} text
 * @return {?}
 */
setText(renderNode: any, text: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} startingStyles
 * @param {?} keyframes
 * @param {?} duration
 * @param {?} delay
 * @param {?} easing
 * @param {?=} previousPlayers
 * @return {?}
 */
animate(
      element: any, startingStyles: any, keyframes: any[], duration: number, delay: number,
      easing: string, previousPlayers?: any[]) {}
}

export const /** @type {?} */ Renderer2Interceptor = new InjectionToken<Renderer2[]>('Renderer2Interceptor');
/**
 * Injectable service that provides a low-level interface for modifying the UI.
 * 
 * Use this service to bypass Angular's templating and make custom UI changes that can't be
 * expressed declaratively. For example if you need to set a property or an attribute whose name is
 * not statically known, use {\@link #setElementProperty} or {\@link #setElementAttribute}
 * respectively.
 * 
 * If you are implementing a custom renderer, you must implement this interface.
 * 
 * The default Renderer implementation is `DomRenderer`. Also available is `WebWorkerRenderer`.
 * 
 * @deprecated Use `RendererFactory2` instead.
 * @abstract
 */
export abstract class RootRenderer {
/**
 * @abstract
 * @param {?} componentType
 * @return {?}
 */
renderComponent(componentType: RenderComponentType) {}
}

/**
 * @experimental
 */
export interface RendererType2 {
  id: string;
  encapsulation: ViewEncapsulation;
  styles: (string|any[])[];
  data: {[kind: string]: any};
}
/**
 * \@experimental
 * @abstract
 */
export abstract class RendererFactory2 {
/**
 * @abstract
 * @param {?} hostElement
 * @param {?} type
 * @return {?}
 */
createRenderer(hostElement: any, type: RendererType2|null) {}
}
export type RendererStyleFlags2 = number;
export let RendererStyleFlags2: any = {};
RendererStyleFlags2.Important = 1;
RendererStyleFlags2.DashCase = 2;
RendererStyleFlags2[RendererStyleFlags2.Important] = "Important";
RendererStyleFlags2[RendererStyleFlags2.DashCase] = "DashCase";

/**
 * \@experimental
 * @abstract
 */
export abstract class Renderer2 {
/**
 * This field can be used to store arbitrary data on this renderer instance.
 * This is useful for renderers that delegate to other renderers.
 * @abstract
 * @return {?}
 */
data() {}
/**
 * @abstract
 * @return {?}
 */
destroy() {}
/**
 * @abstract
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
createElement(name: string, namespace?: string|null) {}
/**
 * @abstract
 * @param {?} value
 * @return {?}
 */
createComment(value: string) {}
/**
 * @abstract
 * @param {?} value
 * @return {?}
 */
createText(value: string) {}
  /**
   * This property is allowed to be null / undefined,
   * in which case the view engine won't call it.
   * This is used as a performance optimization for production mode.
   */
  destroyNode: ((node: any) => void)|null;
/**
 * @abstract
 * @param {?} parent
 * @param {?} newChild
 * @return {?}
 */
appendChild(parent: any, newChild: any) {}
/**
 * @abstract
 * @param {?} parent
 * @param {?} newChild
 * @param {?} refChild
 * @return {?}
 */
insertBefore(parent: any, newChild: any, refChild: any) {}
/**
 * @abstract
 * @param {?} parent
 * @param {?} oldChild
 * @return {?}
 */
removeChild(parent: any, oldChild: any) {}
/**
 * @abstract
 * @param {?} selectorOrNode
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any) {}
/**
 * Attention: On WebWorkers, this will always return a value,
 * as we are asking for a result synchronously. I.e.
 * the caller can't rely on checking whether this is null or not.
 * @abstract
 * @param {?} node
 * @return {?}
 */
parentNode(node: any) {}
/**
 * Attention: On WebWorkers, this will always return a value,
 * as we are asking for a result synchronously. I.e.
 * the caller can't rely on checking whether this is null or not.
 * @abstract
 * @param {?} node
 * @return {?}
 */
nextSibling(node: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @param {?=} namespace
 * @return {?}
 */
setAttribute(el: any, name: string, value: string, namespace?: string|null) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
removeAttribute(el: any, name: string, namespace?: string|null) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
addClass(el: any, name: string) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
removeClass(el: any, name: string) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} style
 * @param {?} value
 * @param {?=} flags
 * @return {?}
 */
setStyle(el: any, style: string, value: any, flags?: RendererStyleFlags2) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} style
 * @param {?=} flags
 * @return {?}
 */
removeStyle(el: any, style: string, flags?: RendererStyleFlags2) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any) {}
/**
 * @abstract
 * @param {?} node
 * @param {?} value
 * @return {?}
 */
setValue(node: any, value: string) {}
/**
 * @abstract
 * @param {?} target
 * @param {?} eventName
 * @param {?} callback
 * @return {?}
 */
listen(
      target: 'window'|'document'|'body'|any, eventName: string,
      callback: (event: any) => boolean | void) {}
}

function Renderer2_tsickle_Closure_declarations() {
/**
 * This property is allowed to be null / undefined,
 * in which case the view engine won't call it.
 * This is used as a performance optimization for production mode.
 * @type {?}
 */
Renderer2.prototype.destroyNode;
}

